import React, { useEffect, useState, useRef } from "react"
import styles from "@/components/Default/Video/v1/VideoControls.module.scss"

const VideoControls = ({
  videoRef,
  id,
  setIsVideoPlaying,
  setVideoPlayed,
  flagState,
  onSeekSync,
  hasCaptions,
}) => {
  const [playing, setPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [current, setCurrent] = useState(0)
  const [muted, setMuted] = useState(true)
  const [volume, setVolume] = useState(0)
  const [showCaptions, setShowCaptions] = useState(false)
  const [hasAudio, setHasAudio] = useState(false)
  const announcementRef = useRef(null)

  const formatTime = sec => {
    if (!sec || isNaN(sec)) return "0:00"
    const minutes = Math.floor(sec / 60)
    const seconds = Math.floor(sec % 60)
      .toString()
      .padStart(2, "0")
    return `${minutes}:${seconds}`
  }

  useEffect(() => {
    const video = videoRef?.current
    if (!video) return

    // default start muted
    video.muted = true
    video.volume = 0

    // AUDIO DETECTION LOGIC
    const detectAudio = () => {
      const audioExists =
        video?.audioTracks?.length > 0 || // Modern browsers
        video?.webkitAudioDecodedByteCount > 0 || // Safari / older Chromium
        video?.mozHasAudio || // Firefox
        video?.msHasAudio // IE/Edge legacy

      setHasAudio(!!audioExists)
    }

    const handleLoadedMetadata = () => {
      setDuration(video.duration || 0)
      detectAudio()
    }

    const handleTimeUpdate = () => setCurrent(video.currentTime || 0)

    const handlePlay = () => {
      setPlaying(true)
      setIsVideoPlaying?.(true)
      setVideoPlayed?.(true)
      if (announcementRef.current) {
        announcementRef.current.textContent = "Video is now playing"
      }
    }

    const handlePause = () => {
      setPlaying(false)
      if (announcementRef.current) {
        announcementRef.current.textContent = "Video is now paused"
      }
    }

    video.addEventListener("loadedmetadata", handleLoadedMetadata)
    video.addEventListener("timeupdate", handleTimeUpdate)
    video.addEventListener("play", handlePlay)
    video.addEventListener("pause", handlePause)

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata)
      video.removeEventListener("timeupdate", handleTimeUpdate)
      video.removeEventListener("play", handlePlay)
      video.removeEventListener("pause", handlePause)
    }
  }, [videoRef])

  const togglePlay = () => {
    const video = videoRef?.current
    if (!video) return
    video.paused ? video.play() : video.pause()
  }

  const handleSeek = e => {
    const time = Number(e.target.value)
    const video = videoRef?.current
    if (video) {
      video.currentTime = time
      onSeekSync?.(time)
      setCurrent(time)
    }
  }

  const toggleMute = () => {
    const video = videoRef?.current
    if (!video) return

    const newMuted = !muted
    setMuted(newMuted)
    video.muted = newMuted

    // If unmuting and volume is 0, restore to 0.5
    if (!newMuted && volume === 0) {
      video.volume = 0.5
      setVolume(0.5)
    }
  }

  const handleVolumeChange = e => {
    const newVolume = Number(e.target.value)
    const video = videoRef?.current
    if (!video) return

    setVolume(newVolume)
    video.volume = newVolume

    if (newVolume === 0) {
      video.muted = true
      setMuted(true)
    } else {
      video.muted = false
      setMuted(false)
    }
  }

  const toggleFullscreen = () => {
    const video = videoRef?.current
    if (!video) return

    if (video.requestFullscreen) video.requestFullscreen()
    else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen()
  }

  const toggleCaptions = () => {
    const video = videoRef?.current
    if (!video) return

    const tracks = video.textTracks || []
    if (tracks.length > 0) {
      const newState = tracks[0].mode === "showing" ? "hidden" : "showing"
      for (let i = 0; i < tracks.length; i++) {
        tracks[i].mode = newState
      }
      setShowCaptions(newState === "showing")
    }
  }

  return (
    <div
      className={styles.VideoControls}
      role="region"
      aria-label="Video player controls"
    >
      {/* Screen Reader Announcements */}
      <div
        ref={announcementRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className={styles.screenReaderAnnouncement}
      />

      {/* Play/Pause Button */}
      <button
        className={styles.playToggle}
        onClick={togglePlay}
        aria-pressed={playing}
        aria-label={playing ? "Pause video" : "Play video"}
      >
        {playing ? (
          <svg width="20" height="20" viewBox="0 0 24 24">
            <rect x="5" y="3" width="4" height="18" />
            <rect x="15" y="3" width="4" height="18" />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24">
            <polygon points="5 3 19 12 5 21" />
          </svg>
        )}
      </button>

      {/* Progress Bar */}
      <div className={styles.progress}>
        <input
          type="range"
          min="0"
          max={duration || 0}
          step="0.1"
          value={current}
          onChange={handleSeek}
          className={styles.progressSlider}
          aria-label="Video progress"
        />
      </div>

      {/* Timestamp */}
      <div className={styles.time}>
        <span>{formatTime(current)}</span>
        <span className={styles.timeSeparator}>/</span>
        <span>{formatTime(duration)}</span>
      </div>

      {/* AUDIO CONTROLS — ONLY IF VIDEO HAS AUDIO */}
      {hasAudio && (
        <div className={styles.audioControls}>
          <button
            className={styles.muteButton}
            onClick={toggleMute}
            aria-label={muted ? "Unmute audio" : "Mute audio"}
          >
            {muted ? "🔇" : "🔊"}
          </button>

          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={volume}
            onChange={handleVolumeChange}
            className={styles.progressSlider}
            aria-label="Audio volume"
          />
        </div>
      )}

      {/* Captions */}
      {hasCaptions && (
        <button
          className={`${styles.captions} ${showCaptions ? styles.active : ""}`}
          onClick={toggleCaptions}
        >
          CC
        </button>
      )}

      {/* Fullscreen */}
      <button
        className={styles.fullscreen}
        onClick={toggleFullscreen}
        aria-label="Full screen"
      >
        <svg width="20" height="20" viewBox="0 0 24 24">
          <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
        </svg>
      </button>
    </div>
  )
}

export default VideoControls
